import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Pencil, Trash2 } from 'lucide-react';
import type { Task } from '@tailoring-crm/shared';
import {
  useDeleteTask,
  useSetTaskStatus,
  type ActivitySubject,
} from '../../lib/activity/use-activity.js';
import { followUpState } from '../../lib/follow-ups/follow-up-state.js';
import { formatDate } from '../../lib/format.js';
import { cn, Badge, Button, useConfirm, useToast } from '@tailoring-crm/ui';

/**
 * One customer's follow-up tasks: a tick to complete or reopen, the due date
 * coloured the way the follow-ups page colours it, and edit / delete.
 *
 * Below `sm` a row stacks. A six-fact row on a phone is a horizontal scroll, and this app is used
 * standing at a counter.
 */
export function TaskList({
  subject,
  tasks,
  readOnly,
  onEdit,
}: {
  subject: ActivitySubject;
  tasks: readonly Task[];
  readOnly: boolean;
  onEdit: (task: Task) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const confirm = useConfirm();
  const setStatus = useSetTaskStatus(subject);
  const remove = useDeleteTask(subject);

  if (tasks.length === 0) {
    return (
      <p className="activity-empty rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
        {t('activity.tasks.empty')}
      </p>
    );
  }

  const toggle = (task: Task): void => {
    setStatus.mutate(
      { taskId: task.id, status: task.status === 'open' ? 'done' : 'open' },
      {
        onSuccess: () =>
          toast({
            title:
              task.status === 'open' ? t('activity.tasks.completed') : t('activity.tasks.reopened'),
            variant: 'success',
          }),
        onError: () => toast({ title: t('error.unexpected'), variant: 'destructive' }),
      },
    );
  };

  const deleteTask = (task: Task): void => {
    void (async () => {
      const confirmed = await confirm({
        title: t('activity.tasks.deleteTitle', { name: task.name }),
        description: t('activity.tasks.deleteDescription'),
        confirmLabel: t('common.delete'),
        destructive: true,
      });
      if (!confirmed) return;
      await remove.mutateAsync(task.id);
      toast({ title: t('activity.tasks.deleted'), variant: 'success' });
    })();
  };

  return (
    <ul className="flex flex-col gap-2">
      {tasks.map((task) => {
        const due = task.status === 'open' ? followUpState(task.dueAt) : 'none';
        const closed = task.status !== 'open';

        return (
          <li
            key={task.id}
            className={cn(
              'activity-task flex flex-col gap-2 rounded-lg border border-border p-3 sm:flex-row sm:items-center sm:gap-4',
              closed && 'opacity-60',
            )}
          >
            {readOnly ? null : (
              <button
                type="button"
                aria-label={
                  task.status === 'open'
                    ? t('activity.tasks.completeAction')
                    : t('activity.tasks.reopenAction')
                }
                onClick={() => toggle(task)}
                className={cn(
                  'inline-flex size-5 shrink-0 items-center justify-center rounded-full border',
                  task.status === 'done'
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-input',
                )}
              >
                {task.status === 'done' ? <Check aria-hidden="true" className="size-3.5" /> : null}
              </button>
            )}

            <div className="min-w-0 flex-1">
              <p className={cn('truncate text-sm font-medium', closed && 'line-through')}>
                {task.name}
              </p>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                {task.categoryLabel ? <span>{task.categoryLabel}</span> : null}
                {task.ownerName ? (
                  <>
                    {task.categoryLabel ? <span aria-hidden="true">·</span> : null}
                    <span>{task.ownerName}</span>
                  </>
                ) : null}
                {task.status === 'cancelled' ? (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>{t('activity.tasks.status.cancelled')}</span>
                  </>
                ) : null}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {task.priority === 'high' ? (
                <Badge variant="outline">{t('activity.tasks.priority.high')}</Badge>
              ) : null}
              {/* The date goes red the day AFTER it passes, drawn from the same helper the
                  header and the follow-ups page use, so one task is never two colours. */}
              <span
                className={cn(
                  'whitespace-nowrap text-sm tabular-nums',
                  due === 'overdue' && 'font-medium text-destructive',
                  due === 'today' && 'font-medium text-amber-700 dark:text-amber-400',
                )}
              >
                {formatDate(task.dueAt)}
              </span>

              {readOnly ? null : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t('activity.tasks.editAction')}
                    onClick={() => onEdit(task)}
                  >
                    <Pencil aria-hidden="true" className="size-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={t('activity.tasks.deleteAction')}
                    onClick={() => deleteTask(task)}
                  >
                    <Trash2 aria-hidden="true" className="size-3.5" />
                  </Button>
                </>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
